import { type UIViewInjectedProps, UIView } from '@uirouter/react';
import { Tabs } from 'antd';
import { observer } from 'mobx-react-lite';
import { useEffect, useState, useCallback } from 'react';
import { runInAction } from 'mobx';

import { FormattedMessage } from 'react-intl';
import { Drawer } from '@/components/drawer';
import { Loader } from '@/components/loader';
import { waybillStore, waybillWeighingAnalysisStore } from '@/stores';
import { api } from '@/api';
import { showError } from '@/utils/common';

import { WeighingAnalyses } from './weighing-analyses';
import { Farmers } from './farmers';
import { Lots } from './lots';

const MAX_WIDTH = 1300;
const { TabPane } = Tabs;

export const WaybillView = observer(({ transition }: UIViewInjectedProps) => {
  const waybillId = +transition.router.globals.params?.waybillId;
  const [isLoading, setIsLoading] = useState(true);

  const waybill = waybillStore.items.find(({ id }: any) => id === waybillId);
  const title = `Waybill #${waybill?.waybillNumber ?? waybill?.id ?? ''}`;

  const onClose = useCallback(() => {
    transition.router.stateService.go('base-layout.waybills');
  }, [transition]);

  useEffect(() => {
    if (!waybillId) return;

    waybillWeighingAnalysisStore.clear();
    setIsLoading(true);
    const request = api.waybills.get(waybillId);

    request.source
      .then(async ({ data }) => {
        runInAction(() => {
          const waybillInStore = waybillStore.items.find(({ id }) => id === waybillId);
          if (waybillInStore) {
            waybillStore.updateItem(data);
          } else {
            waybillStore.addItem(data);
          }
        });
        await waybillWeighingAnalysisStore.load(waybillId);
        setIsLoading(false);
      })
      .catch((error) => {
        showError(error);
        onClose();
        setIsLoading(false);
      })
      .finally(() => {
        request.cancel();
      });

    return () => {
      request.cancel();
      waybillWeighingAnalysisStore.clear();
    };
  }, [waybillId, onClose]);

  const width = window.innerWidth - 250 > MAX_WIDTH ? MAX_WIDTH : window.innerWidth - 250;

  if (isLoading) {
    return (
      <Drawer onClose={onClose} visible={true} title={`Waybill #${waybillId}`} width={width}>
        <Loader />
      </Drawer>
    );
  }

  if (!waybill) {
    return null;
  }

  return (
    <>
      <Drawer onClose={onClose} visible={true} title={title} width={width}>
        <Tabs defaultActiveKey="weighing-analyses">
          <TabPane tab="Weighing Analyses" key="weighing-analyses">
            <WeighingAnalyses waybillId={waybillId} />
          </TabPane>
          <TabPane tab="Farmers" key="farmers">
            <Farmers waybill={waybill} />
          </TabPane>
          <TabPane tab={<FormattedMessage id="waybills.tabs.lots" />} key="lots">
            <Lots waybill={waybill} />
          </TabPane>
        </Tabs>
      </Drawer>
      <UIView />
    </>
  );
});
